import cx from "classnames"
import { RefreshCw } from "lucide-react"
import { observer } from "mobx-react"
import { useEffect } from "react"
import type { ProviderAccountLimit, ProviderAccountUsage, ProviderAccountUsagePace, ProviderAccountUsagePeriod } from "../../nest/generated"
import type { CodeStore } from "../../store/store"
import type { AccountUsageItem, AccountUsageState } from "../../store/managers/AccountUsageManager"

const ACCOUNT_USAGE_DAY_MS = 24 * 60 * 60 * 1000

function formatCost(cost: number): string {
    if (cost === 0) return "$0.00"
    if (cost < 0.01) return `$${cost.toFixed(4)}`
    return `$${cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatTokens(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
    return n.toLocaleString()
}

function formatAccountNumber(value: number): string {
    if (!Number.isFinite(value)) return "0"
    if (Math.abs(value) >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`
    return formatTokens(value)
}

function formatPercent(value: number | undefined): string {
    if (value === undefined || !Number.isFinite(value)) return "Unknown"
    const clamped = Math.max(0, Math.min(100, value))
    return `${Math.round(clamped)}%`
}

function formatRelativeTime(targetIso: string | undefined, now = new Date()): string | undefined {
    if (!targetIso) return undefined
    const target = new Date(targetIso)
    if (Number.isNaN(target.getTime())) return undefined
    const deltaMs = target.getTime() - now.getTime()
    const absMs = Math.abs(deltaMs)
    const day = Math.floor(absMs / ACCOUNT_USAGE_DAY_MS)
    const hour = Math.floor((absMs % ACCOUNT_USAGE_DAY_MS) / (60 * 60 * 1000))
    const minute = Math.floor((absMs % (60 * 60 * 1000)) / (60 * 1000))

    const parts: string[] = []
    if (day > 0) parts.push(`${day}d`)
    if (hour > 0 && parts.length < 2) parts.push(`${hour}h`)
    if (parts.length < 2 && minute > 0) parts.push(`${minute}m`)
    if (parts.length === 0) parts.push("<1m")
    return deltaMs >= 0 ? `in ${parts.join(" ")}` : `${parts.join(" ")} ago`
}

function accountStatusLabel(item: AccountUsageItem): string {
    if (item.error) return "error"
    if (!item.usage) return "unknown"
    return item.usage.status
}

function accountStatusClass(item: AccountUsageItem): string {
    switch (accountStatusLabel(item)) {
        case "available":
            return "border-success/30 bg-success/10 text-success"
        case "partial":
            return "border-warning/30 bg-warning/10 text-warning"
        case "unavailable":
        case "error":
            return "border-error/30 bg-error/10 text-error"
        default:
            return "border-border bg-base-200 text-muted"
    }
}

function limitPercentLeft(limit: ProviderAccountLimit): number | undefined {
    if (limit.percentLeft !== undefined) return limit.percentLeft
    if (limit.usedPercent !== undefined) return 100 - limit.usedPercent
    return undefined
}

function limitUsedPercent(limit: ProviderAccountLimit): number | undefined {
    if (limit.usedPercent !== undefined) return limit.usedPercent
    if (limit.percentLeft !== undefined) return 100 - limit.percentLeft
    return undefined
}

function paceText(pace: ProviderAccountUsagePace | undefined): string | undefined {
    if (!pace) return undefined
    if (pace.state === "reserve" && pace.reservePercent !== undefined) return `${formatPercent(pace.reservePercent)} reserve`
    if (pace.state === "deficit" && pace.deficitPercent !== undefined) return `${formatPercent(pace.deficitPercent)} deficit`
    if (pace.state === "on_track") return "on pace"
    return undefined
}

function paceClass(pace: ProviderAccountUsagePace | undefined): string {
    if (!pace) return "text-muted"
    if (pace.state === "reserve" || pace.state === "on_track") return "text-success"
    return "text-warning"
}

function periodLabel(id: string): string {
    switch (id) {
        case "today":
            return "Today"
        case "7d":
            return "7d"
        case "30d":
            return "30d"
        default:
            return id
    }
}

export function periodTokenTotal(period: ProviderAccountUsagePeriod): number | undefined {
    if (period.tokens?.total !== undefined) return period.tokens.total
    const total = (period.tokens?.input ?? 0) + (period.tokens?.output ?? 0)
    return total > 0 ? total : undefined
}

function currencyPrefix(currency: string | undefined): string {
    return currency?.toUpperCase() === "USD" || !currency ? "$" : `${currency.toUpperCase()} `
}

function formatCreditAmount(value: number, currency: string | undefined): string {
    return `${currencyPrefix(currency)}${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
}

function providerAccountSubtitle(usage: ProviderAccountUsage | undefined): string {
    if (!usage?.account) return ""
    return [usage.account.email, usage.account.organization, usage.account.plan, usage.account.id ? `ID ${usage.account.id}` : undefined]
        .filter(Boolean)
        .join(" · ")
}

function accountUsageMessage(item: AccountUsageItem): string {
    if (item.error) return item.error
    if (!item.usage) return "No account usage data"
    if (item.usage.message) return item.usage.message
    const diagnostic = item.usage.diagnostics?.[0]
    if (diagnostic) return diagnostic.message
    return "No account usage data"
}

function cleanResetDescription(value: string | undefined): string | undefined {
    let text = value?.trim().replace(/\s+/g, " ")
    if (!text) return undefined
    while (/^resets?\s+/i.test(text)) {
        text = text.replace(/^resets?\s+/i, "").trim()
    }
    return text || undefined
}

function limitResetText(limit: ProviderAccountLimit): string {
    const relative = formatRelativeTime(limit.resetsAt)
    if (relative) return `Resets ${relative}`
    const description = cleanResetDescription(limit.resetDescription)
    if (description) return `Resets ${description}`
    return "Reset unknown"
}

export const StatsTab = observer(({ store }: { store: CodeStore }) => {
    useEffect(() => {
        void store.accountUsage.ensureLoaded()
    }, [store])

    return <AccountUsageSection state={store.accountUsage.state} onRefresh={() => void store.accountUsage.refresh()} />
})

function AccountUsageSection({ state, onRefresh }: { state: AccountUsageState; onRefresh: () => void }) {
    const isLoading = state.status === "idle" || state.status === "loading"
    const updatedText = state.loadedAt ? formatRelativeTime(state.loadedAt) : undefined
    const statusText =
        state.status === "error" && state.items.length === 0
            ? "Account stats unavailable"
            : state.status === "error"
              ? `${state.items.length} ${state.items.length === 1 ? "provider" : "providers"} · refresh failed`
              : isLoading && state.items.length === 0
                ? "Loading account stats"
                : `${state.items.length} ${state.items.length === 1 ? "provider" : "providers"}`

    return (
        <section className="flex min-h-full flex-col gap-3 p-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-[10px] text-muted uppercase tracking-widest font-medium">Accounts</div>
                    <div className="text-sm font-semibold text-base-content mt-0.5">{statusText}</div>
                    {updatedText && <div className="text-xs text-muted mt-0.5">Loaded {updatedText}</div>}
                </div>
                <button
                    type="button"
                    className="btn flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-colors border shrink-0 bg-base-100 text-muted hover:text-base-content border-border disabled:opacity-50"
                    onClick={onRefresh}
                    disabled={isLoading}
                    title="Refresh account stats"
                >
                    <RefreshCw size={12} className={cx(isLoading && "animate-spin")} />
                    <span>{isLoading ? "Refreshing" : "Refresh"}</span>
                </button>
            </div>

            {state.status === "error" && <div className="border border-error/30 bg-error/10 px-2 py-1.5 text-xs text-error">{state.error}</div>}

            {state.items.length > 0 && (
                <div className="grid flex-1 items-start gap-3 xl:grid-cols-2">
                    {state.items.map((item) => (
                        <AccountUsageProviderPanel key={item.providerId} item={item} />
                    ))}
                </div>
            )}
        </section>
    )
}

function AccountUsageProviderPanel({ item }: { item: AccountUsageItem }) {
    const usage = item.usage
    const subtitle = providerAccountSubtitle(usage)
    const periods = usage?.periods ?? []
    const limits = usage?.limits ?? []
    const staleDiagnostic = usage?.diagnostics?.find(
        (diagnostic) => diagnostic.code === "stale_remote_snapshot" || diagnostic.code === "stale_local_usage_snapshot"
    )

    return (
        <div className="border border-border bg-base-100">
            <div className="flex items-start justify-between gap-2 border-b border-border bg-base-200/70 px-2.5 py-1.5">
                <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                        <div className="truncate text-sm font-semibold text-base-content">{item.label}</div>
                        {usage?.source && <div className="shrink-0 text-[10px] uppercase tracking-wide text-muted">{usage.source}</div>}
                    </div>
                    {subtitle && <div className="mt-0.5 truncate text-[10px] text-muted">{subtitle}</div>}
                </div>
                <div className={cx("shrink-0 border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide", accountStatusClass(item))}>
                    {accountStatusLabel(item)}
                </div>
            </div>

            <div className="flex flex-col gap-2 p-2.5">
                <AccountUsageSectionLabel label="Meters" updatedAt={usage?.limitsUpdatedAt} />
                <div className="flex flex-col gap-1.5">
                    {limits.length > 0 ? (
                        limits.map((limit) => <AccountLimitRow key={limit.id} limit={limit} />)
                    ) : (
                        <div className="border border-border bg-base-200/60 px-2 py-1.5 text-xs text-muted">{accountUsageMessage(item)}</div>
                    )}
                </div>
                {staleDiagnostic && (
                    <div className="border border-warning/30 bg-warning/10 px-2 py-1.5 text-[10px] text-warning">{staleDiagnostic.message}</div>
                )}

                {periods.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                        <AccountUsageSectionLabel label="Usage" updatedAt={usage?.periodsUpdatedAt} />
                        <div className="grid grid-cols-3 gap-px bg-border">
                            {periods.map((period) => (
                                <AccountPeriodStat key={period.id} period={period} />
                            ))}
                        </div>
                    </div>
                )}

                <AccountCreditsRow usage={usage} />
                <AccountScanRow usage={usage} />
            </div>
        </div>
    )
}

function AccountUsageSectionLabel({ label, updatedAt }: { label: string; updatedAt?: string }) {
    const relative = formatRelativeTime(updatedAt)
    return (
        <div className="flex items-center justify-between gap-2 text-[9px] font-medium tracking-wide text-muted uppercase">
            <span>{label}</span>
            {relative && <span className="normal-case tracking-normal">Updated {relative}</span>}
        </div>
    )
}

function AccountLimitRow({ limit }: { limit: ProviderAccountLimit }) {
    const percentLeft = limitPercentLeft(limit)
    const usedPercent = limitUsedPercent(limit)
    const fillWidth = `${Math.max(0, Math.min(100, percentLeft ?? 0))}%`
    const pace = paceText(limit.pace)
    const barClass = percentLeft === undefined ? "bg-base-content/20" : percentLeft <= 15 ? "bg-error" : percentLeft <= 35 ? "bg-warning" : "bg-primary"

    return (
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 gap-y-1">
            <div className="min-w-0">
                <div className="truncate text-xs font-semibold text-base-content">{limit.label}</div>
                <div className="truncate text-[10px] text-muted">
                    {[limit.kind, limit.model].filter(Boolean).join(" · ")}
                    {usedPercent !== undefined ? ` · ${formatPercent(usedPercent)} used` : ""}
                </div>
            </div>
            <div className="text-right text-sm font-bold text-base-content">{formatPercent(percentLeft)}</div>
            <div className="col-span-2 h-1.5 bg-base-300">
                <div className={cx("h-full", barClass)} style={{ width: fillWidth }} />
            </div>
            <div className="col-span-2 flex items-center justify-between gap-3 text-[10px]">
                <span className="truncate text-muted">{limitResetText(limit)}</span>
                {pace && <span className={cx("shrink-0 font-semibold", paceClass(limit.pace))}>{pace}</span>}
            </div>
        </div>
    )
}

function AccountPeriodStat({ period }: { period: ProviderAccountUsagePeriod }) {
    const totalTokens = periodTokenTotal(period)
    const cost = period.costUsd !== undefined ? formatCost(period.costUsd) : undefined
    const secondary =
        totalTokens !== undefined
            ? `${formatAccountNumber(totalTokens)} tokens`
            : period.requests !== undefined
              ? `${period.requests.toLocaleString()} ${period.requests === 1 ? "request" : "requests"}`
              : "No usage"

    return (
        <div className="bg-base-100 px-2 py-1.5">
            <div className="text-muted text-[9px] uppercase tracking-wide font-medium">{periodLabel(period.id)}</div>
            <div className="text-base-content text-sm font-bold leading-tight mt-0.5">{cost ?? "—"}</div>
            <div className="mt-0.5 truncate text-[10px] text-muted">{secondary}</div>
        </div>
    )
}

function AccountCreditsRow({ usage }: { usage: ProviderAccountUsage | undefined }) {
    const credits = usage?.credits
    if (!credits) return null

    const stats: Array<{ label: string; value: string }> = []
    if (credits.unlimited) stats.push({ label: "Credits", value: "Unlimited" })
    if (credits.remaining !== undefined) stats.push({ label: "Remaining", value: formatCreditAmount(credits.remaining, credits.currency) })
    if (credits.used !== undefined) stats.push({ label: "Used", value: formatCreditAmount(credits.used, credits.currency) })
    if (credits.limit !== undefined) stats.push({ label: "Limit", value: formatCreditAmount(credits.limit, credits.currency) })
    if (credits.manualResetsAvailable !== undefined) stats.push({ label: "Manual resets", value: credits.manualResetsAvailable.toLocaleString() })
    if (credits.nextManualResetExpiresAt) {
        const relative = formatRelativeTime(credits.nextManualResetExpiresAt)
        if (relative) stats.push({ label: "Next expires", value: relative })
    }
    if (credits.resetsAt) {
        const relative = formatRelativeTime(credits.resetsAt)
        if (relative) stats.push({ label: "Resets", value: relative })
    }
    if (stats.length === 0) return null

    return (
        <div className="flex flex-col gap-1.5">
            <div className="text-[9px] font-medium tracking-wide text-muted uppercase">Balance</div>
            <div className="grid grid-cols-2 gap-px bg-border">
                {stats.map((stat) => (
                    <div key={`${stat.label}:${stat.value}`} className="bg-base-100 px-2 py-1.5">
                        <div className="text-muted text-[9px] uppercase tracking-wide font-medium">{stat.label}</div>
                        <div className="truncate text-base-content text-sm font-bold leading-tight mt-0.5">{stat.value}</div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function AccountScanRow({ usage }: { usage: ProviderAccountUsage | undefined }) {
    if (!usage?.scan || usage.scan.requestedFiles === 0) return null
    const failureText = usage.scan.failedFiles > 0 ? ` · ${usage.scan.failedFiles} failed` : ""
    return (
        <div className="text-[10px] text-muted">
            Local logs {usage.scan.succeededFiles.toLocaleString()}/{usage.scan.requestedFiles.toLocaleString()}
            {failureText}
        </div>
    )
}
